/**
 * E002-T018: TableSelector Component
 * E012-T018: RBAC Database Visibility Filter
 * E018-S004-T002: External Source Option
 * E018-S008-T005: CrossDoc Badge Indicator
 * S020-8.20.4: Permission-based Filtering (read/write)
 *
 * Dropdown-Komponente zur Auswahl einer Database (Table).
 * Zeigt alle verfügbaren Databases im Workspace, gruppiert nach Doc.
 *
 * RBAC (E012-T018): Verwendet accessibleDatabases$ statt databases$
 * um nur Databases anzuzeigen auf die der User Lesezugriff hat.
 *
 * S020-8.20.4: requiredPermission prop steuert ob Read oder Write Permission
 * geprüft wird. Default ist 'read' für Rückwärtskompatibilität.
 *
 * External Source (E018-S004-T002): Wenn showExternalOption=true, wird
 * "External Source" als erste Option mit Trennstrich angezeigt.
 *
 * CrossDoc Badge (E018-S008-T005): Tables in anderen Docs zeigen ein LinkedPageIcon Badge.
 */

import { Divider, Menu, MenuItem, MenuSub, Tooltip } from '@affine/component';
import { useI18n } from '@affine/i18n';
import {
  DatabaseTableViewIcon,
  LinkedPageIcon,
  SearchIcon,
  SignalIcon,
} from '@blocksuite/icons/rc';
import { cssVarV2 } from '@streamlain/theme/v2';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import type { FC } from 'react';
import { useMemo, useState } from 'react';

import { DocService } from '../../../doc';
import {
  filterDocGroupsForHiddenMailFolderDocIds,
  useHiddenMailFolderDocIds,
} from '../../mail/mail-folder-visibility-filter';
import {
  DatabaseDiscoveryService,
  type DatabaseInfo,
} from '../../services/database-discovery';
import * as styles from '../automation-panel.css';
import { computeSourceSelectorDisplayText } from './table-selector-display-text';

/**
 * E018-S008-T005: CrossDoc Badge Component
 * Shows a link icon when a table is in a different document.
 */
const CrossDocBadge: FC = () => (
  <Tooltip content="Table in another document">
    <span
      style={{
        marginLeft: '4px',
        opacity: 0.6,
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      <LinkedPageIcon style={{ width: '14px', height: '14px' }} />
    </span>
  </Tooltip>
);

// sl-za0v.12.3: EXTERNAL_SOURCE_ID removed. Use triggerConfig.type === 'webhook' instead.

interface DocGroup {
  docId: string;
  docTitle: string;
  databases: DatabaseInfo[];
}

interface SourceTableOption {
  /** Source table info for "Same as Source" option */
  sourceTable: DatabaseInfo;
  /** Label for the option (e.g., "Same as Source (TableName)") */
  label: string;
}

export interface WebhookOption {
  credentialId: string;
  name: string;
}

export interface TableSelectorProps {
  /** Currently selected database block ID */
  value: string | null | undefined;
  /** Callback when a database is selected */
  onChange: (database: DatabaseInfo) => void;
  /** Placeholder text when no database is selected */
  placeholder?: string;
  /** Block ID to exclude from the list (e.g., source table when showing "Same as Source" option) */
  excludeBlockId?: string | null;
  /** sl-optc: Multiple block IDs to exclude (e.g., already selected tables in multi-output) */
  excludeBlockIds?: string[];
  /** Optional "Same as Source" option to show at the top */
  sourceTableOption?: SourceTableOption;
  /** E018-S004-T002: Show "External Source" option at the top */
  showExternalOption?: boolean;
  /** E018-S004-T002: Callback when "External Source" is selected */
  onExternalSelect?: () => void;
  /**
   * S020-8.20.4: Required permission level for displayed tables.
   * - 'read': Show tables user can read (default, for Input Tab)
   * - 'write': Show tables user can write (for Output Tab)
   */
  requiredPermission?: 'read' | 'write';
  /** RBAC-filtered workspace webhooks shown as a group above tables. */
  webhookOptions?: WebhookOption[];
  /** Currently selected webhook credential ID (when a webhook is the lead source). */
  selectedWebhookId?: string | null;
  /** Callback when a webhook is selected from the dropdown. */
  onWebhookSelect?: (credentialId: string) => void;
}

export const TableSelector: FC<TableSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Select a table...',
  excludeBlockId,
  excludeBlockIds = [],
  sourceTableOption,
  showExternalOption: _showExternalOption,
  onExternalSelect: _onExternalSelect,
  requiredPermission = 'read',
  webhookOptions,
  selectedWebhookId,
  onWebhookSelect,
}) => {
  const t = useI18n();
  const [searchQuery, setSearchQuery] = useState('');
  const [webhookSearchQuery, setWebhookSearchQuery] = useState('');
  const [subSearchQueries, setSubSearchQueries] = useState<
    Record<string, string>
  >({});
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const discoveryService = useService(DatabaseDiscoveryService);
  // E018-S008-T005: Get current doc ID for CrossDoc detection
  const docService = useServiceOptional(DocService);
  const currentDocId = docService?.doc.id;

  // S020-8.20.4: Select the appropriate permission-filtered stream
  // - 'read': accessibleDatabases$ (Doc_Read permission)
  // - 'write': writableDatabases$ (Doc_Update permission)
  const rawDatabasesByDoc = useLiveData(
    requiredPermission === 'write'
      ? discoveryService.writableDatabasesByDoc$
      : discoveryService.accessibleDatabasesByDoc$
  );
  const rawDatabases = useLiveData(
    requiredPermission === 'write'
      ? discoveryService.writableDatabases$
      : discoveryService.accessibleDatabases$
  );
  const hiddenMailFolderDocIds = useHiddenMailFolderDocIds();
  const visibleDatabasesByDoc = useMemo(
    () =>
      filterDocGroupsForHiddenMailFolderDocIds(
        rawDatabasesByDoc,
        hiddenMailFolderDocIds
      ),
    [hiddenMailFolderDocIds, rawDatabasesByDoc]
  );
  const databases = useMemo(
    () => rawDatabases.filter(db => !hiddenMailFolderDocIds.has(db.docId)),
    [hiddenMailFolderDocIds, rawDatabases]
  );

  // sl-optc: Build set of all excluded block IDs
  const excludedIds = useMemo(() => {
    const ids = new Set<string>(excludeBlockIds);
    if (excludeBlockId) ids.add(excludeBlockId);
    return ids;
  }, [excludeBlockId, excludeBlockIds]);

  // Filter out excluded blocks from the list
  const databasesByDoc = useMemo(() => {
    if (excludedIds.size === 0) return visibleDatabasesByDoc;

    return visibleDatabasesByDoc
      .map((docGroup: DocGroup) => ({
        ...docGroup,
        databases: docGroup.databases.filter(
          (db: DatabaseInfo) => !excludedIds.has(db.blockId)
        ),
      }))
      .filter((docGroup: DocGroup) => docGroup.databases.length > 0);
  }, [visibleDatabasesByDoc, excludedIds]);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredDatabasesByDoc = useMemo(() => {
    if (!normalizedQuery) return databasesByDoc;
    return databasesByDoc
      .map((docGroup: DocGroup) => {
        const docMatches = docGroup.docTitle
          .toLowerCase()
          .includes(normalizedQuery);
        if (docMatches) {
          return docGroup;
        }
        const filteredDatabases = docGroup.databases.filter(
          (db: DatabaseInfo) =>
            db.databaseTitle.toLowerCase().includes(normalizedQuery)
        );
        return {
          ...docGroup,
          databases: filteredDatabases,
        };
      })
      .filter((docGroup: DocGroup) => docGroup.databases.length > 0);
  }, [databasesByDoc, normalizedQuery]);

  const currentDocGroup = currentDocId
    ? filteredDatabasesByDoc.find(group => group.docId === currentDocId)
    : undefined;
  const otherDocGroups = currentDocId
    ? filteredDatabasesByDoc.filter(group => group.docId !== currentDocId)
    : filteredDatabasesByDoc;
  const filteredWebhookOptions = useMemo(() => {
    const normalizedWebhookQuery = webhookSearchQuery.trim().toLowerCase();
    const globallyFiltered = normalizedQuery
      ? (webhookOptions ?? []).filter(wh =>
          wh.name.toLowerCase().includes(normalizedQuery)
        )
      : (webhookOptions ?? []);
    if (!normalizedWebhookQuery) return globallyFiltered;
    return globallyFiltered.filter(wh =>
      wh.name.toLowerCase().includes(normalizedWebhookQuery)
    );
  }, [webhookOptions, normalizedQuery, webhookSearchQuery]);

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchBlur = () => {
    requestAnimationFrame(() => {
      const activeElement = document.activeElement;
      if (
        !(activeElement instanceof HTMLElement) ||
        activeElement.dataset.streamlainSearch !== 'true'
      ) {
        setIsSearchFocused(false);
      }
    });
  };

  const pointerFocusGuardProps = isSearchFocused
    ? {
        onPointerMove: (event: { preventDefault: () => void }) =>
          event.preventDefault(),
        onPointerLeave: (event: { preventDefault: () => void }) =>
          event.preventDefault(),
      }
    : undefined;

  // Find currently selected database info
  const selectedDatabase = value
    ? databases.find((db: DatabaseInfo) => db.blockId === value)
    : null;

  // Check if "Same as Source" is selected
  const isSourceSelected =
    sourceTableOption && value === sourceTableOption.sourceTable.blockId;

  // E018-S008-T005: Check if selected database is CrossDoc
  const isSelectedCrossDoc =
    selectedDatabase && currentDocId && selectedDatabase.docId !== currentDocId;

  const selectedWebhook = selectedWebhookId
    ? (webhookOptions ?? []).find(w => w.credentialId === selectedWebhookId)
    : undefined;

  const displayText = computeSourceSelectorDisplayText({
    selectedWebhookName: selectedWebhook?.name ?? null,
    sourceTableLabel: isSourceSelected
      ? (sourceTableOption?.label ?? null)
      : null,
    selectedDatabase: selectedDatabase ?? null,
    isCrossDoc: Boolean(isSelectedCrossDoc),
    placeholder,
  });

  const renderSearchItem = (
    query: string,
    onQueryChange: (v: string) => void
  ) => (
    <MenuItem
      onSelect={event => event.preventDefault()}
      className={styles.menuSearchItem}
      {...pointerFocusGuardProps}
    >
      <div
        className={styles.menuSearchContent}
        onClick={event => event.stopPropagation()}
        onKeyDown={event => event.stopPropagation()}
      >
        <SearchIcon className={styles.menuSearchIcon} />
        <input
          type="text"
          value={query}
          onChange={event => onQueryChange(event.target.value)}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          placeholder={
            t?.['com.streamlain.streamlain.input.search-sources']?.() ??
            'Quellen suchen...'
          }
          className={styles.menuSearchInput}
          data-streamlain-search="true"
        />
      </div>
    </MenuItem>
  );

  const renderDocGroupItems = (docGroup: DocGroup) => {
    const rawQuery = subSearchQueries[docGroup.docId] ?? '';
    const groupQuery = rawQuery.trim().toLowerCase();
    const filteredDatabases = groupQuery
      ? docGroup.databases.filter((db: DatabaseInfo) =>
          db.databaseTitle.toLowerCase().includes(groupQuery)
        )
      : docGroup.databases;

    const handleSubSearchChange = (value: string) => {
      setSubSearchQueries(prev => ({
        ...prev,
        [docGroup.docId]: value,
      }));
    };

    return (
      <>
        {renderSearchItem(rawQuery, handleSubSearchChange)}
        <Divider />
        {filteredDatabases.length === 0 ? (
          <MenuItem disabled {...pointerFocusGuardProps}>
            No tables found
          </MenuItem>
        ) : (
          filteredDatabases.map((db: DatabaseInfo) => (
            <MenuItem
              key={db.blockId}
              prefixIcon={<DatabaseTableViewIcon />}
              selected={db.blockId === value}
              onClick={() => onChange(db)}
              {...pointerFocusGuardProps}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {db.databaseTitle}
                {currentDocId && docGroup.docId !== currentDocId && (
                  <CrossDocBadge />
                )}
              </span>
            </MenuItem>
          ))
        )}
      </>
    );
  };

  const renderWebhookItems = () => (
    <>
      {renderSearchItem(webhookSearchQuery, setWebhookSearchQuery)}
      <Divider />
      {filteredWebhookOptions.length === 0 ? (
        <MenuItem disabled {...pointerFocusGuardProps}>
          {(webhookOptions ?? []).length === 0
            ? (t['com.streamlain.streamlain.input.no-webhooks']?.() ??
              'No webhooks yet')
            : (t['com.streamlain.streamlain.input.no-webhooks-found']?.() ??
              'No webhooks found')}
        </MenuItem>
      ) : (
        filteredWebhookOptions.map(wh => (
          <MenuItem
            key={wh.credentialId}
            prefixIcon={<SignalIcon />}
            selected={wh.credentialId === selectedWebhookId}
            onClick={() => onWebhookSelect?.(wh.credentialId)}
            {...pointerFocusGuardProps}
          >
            {wh.name}
          </MenuItem>
        ))
      )}
    </>
  );

  const menuItems = (
    <>
      {renderSearchItem(searchQuery, setSearchQuery)}
      <Divider />

      {/* Workspace webhooks group — shown when webhookOptions provided */}
      {webhookOptions && (
        <>
          <MenuSub
            items={renderWebhookItems()}
            triggerOptions={pointerFocusGuardProps}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <SignalIcon style={{ width: '16px', height: '16px' }} />
              {t['com.streamlain.streamlain.input.webhooks']?.() ?? 'Webhooks'}
            </span>
          </MenuSub>
          <Divider />
        </>
      )}

      {/* "Same as Source" option */}
      {sourceTableOption && (
        <>
          <MenuItem
            prefixIcon={<DatabaseTableViewIcon />}
            selected={isSourceSelected}
            onClick={() => onChange(sourceTableOption.sourceTable)}
            {...pointerFocusGuardProps}
          >
            {sourceTableOption.label}
          </MenuItem>
          <Divider />
        </>
      )}

      {/* Other tables */}
      {filteredDatabasesByDoc.length === 0 ? (
        <MenuItem disabled {...pointerFocusGuardProps}>
          No tables found
        </MenuItem>
      ) : (
        <>
          {currentDocGroup && (
            <>
              <MenuSub
                key={currentDocGroup.docId}
                items={renderDocGroupItems(currentDocGroup)}
                triggerOptions={pointerFocusGuardProps}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <LinkedPageIcon
                    style={{
                      width: '16px',
                      height: '16px',
                      marginRight: '8px',
                    }}
                  />
                  {currentDocGroup.docTitle}
                </span>
              </MenuSub>
              {otherDocGroups.length > 0 && <Divider />}
            </>
          )}
          {otherDocGroups.map((docGroup: DocGroup) => {
            const isCrossDocGroup =
              currentDocId && docGroup.docId !== currentDocId;

            return (
              <MenuSub
                key={docGroup.docId}
                items={renderDocGroupItems(docGroup)}
                triggerOptions={pointerFocusGuardProps}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <LinkedPageIcon
                    style={{
                      width: '16px',
                      height: '16px',
                      marginRight: '8px',
                    }}
                  />
                  {docGroup.docTitle}
                  {isCrossDocGroup && <CrossDocBadge />}
                </span>
              </MenuSub>
            );
          })}
        </>
      )}

      {/* sl-za0v.12.3: External Source option removed — use webhook trigger type instead */}
    </>
  );

  return (
    <Menu items={menuItems}>
      <button
        className={styles.fieldRow}
        style={{ cursor: 'pointer', width: '100%' }}
      >
        {selectedWebhook ? (
          <SignalIcon style={{ flexShrink: 0, opacity: 0.6 }} />
        ) : (
          <DatabaseTableViewIcon style={{ flexShrink: 0, opacity: 0.6 }} />
        )}
        <span
          className={styles.fieldLabel}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {displayText}
          {isSelectedCrossDoc && <CrossDocBadge />}
        </span>
        <span style={{ color: cssVarV2('text/tertiary') }}>▼</span>
      </button>
    </Menu>
  );
};
